import { ReactNode } from "react";
import withLayout from "../utils/withLayout";
import NotFound from "../pages/ErrorPages/NotFound";
import Forbidden from "../pages/ErrorPages/Forbidden";
import Unauthorized from "../pages/ErrorPages/Unauthorized";
import Home from "../pages";
import UsersPage from "../pages/users";
import ClientsPage from "../pages/clients";
import ParticipantProfilePage from "../pages/clients/profile";
import GoalsPage from "../pages/clients/profile/goals";
import rewardsConfigPage from "../pages/rewardsConfiguration";
import OrganizationsPage from "../pages/organizations";
import OrganizationLabelsPage from "../pages/organizationLabels";
import ActionsPage from "../pages/actions";
import RolesPage from "../pages/roles";
import ProgramsPage from "../pages/programs";
import SettingsPage from "../pages/settings";
import TreatmentAgenciesPage from "../pages/treatmentAgencies";
import CategoriesPage from "../pages/categories";
import RewardsPage from "@/pages/clients/profile/rewards";
import DashboardPage from "@/pages/dashboard";
import JournalsPage from "@/pages/clients/profile/journals";
import ScreeningColorsPage from "@/pages/screeningColors";
import ExportPage from "@/pages/export";

export interface RouteItem {
  path: string; // Path of the route
  element?: ReactNode; // Element to render for this route (optional for parent routes with children)
  requiresAuth?: boolean; // Whether authentication is required
  requiredActions?: string[]; // Required actions to access the route
  children?: RouteItem[]; // Nested routes (children)
}

//TODO: we should extract the known routes from the db so they are dynamic
export const routesConfig = [
  {
    path: "/",
    element: withLayout(Home)({}),
    requiresAuth: false,
    requiredActions: ["DASHBOARD_VIEW"],
  },
  {
    path: "/participants",
    element: withLayout(ClientsPage)({}),
    requiredActions: ["PARTICIPANTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId",
    element: withLayout(ParticipantProfilePage)({}),
    requiredActions: ["PARTICIPANTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/goals",
    element: withLayout(GoalsPage)({}),
    requiredActions: ["PARTICIPANT_GOALS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/users",
    element: withLayout(UsersPage)({}),
    requiredActions: ["USERS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/rewardsConfiguration",
    element: withLayout(rewardsConfigPage)({}),
    requiredActions: ["REWARDS_CONFIGURATION_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/organizations",
    element: withLayout(OrganizationsPage)({}),
    requiredActions: ["ORGANIZATIONS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/organization-labels",
    element: withLayout(OrganizationLabelsPage)({}),
    requiredActions: ["ORGANIZATION_LABELS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/actions",
    element: withLayout(ActionsPage)({}),
    requiredActions: ["ACTIONS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/roles",
    element: withLayout(RolesPage)({}),
    requiredActions: ["ROLES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/dashboard",
    element: withLayout(DashboardPage)({}),
    requiredActions: ["DASHBOARD_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/programs",
    element: withLayout(ProgramsPage)({}),
    requiredActions: ["PROGRAMS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/settings",
    element: withLayout(SettingsPage)({}),
    requiredActions: ["SETTINGS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/treatment-agencies",
    element: withLayout(TreatmentAgenciesPage)({}),
    requiredActions: ["TREATMENT_AGENCIES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/categories",
    element: withLayout(CategoriesPage)({}),
    requiredActions: ["CATEGORIES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/reimbursements",
    element: withLayout(RewardsPage)({}),
    requiredActions: ["PARTICIPANT_REWARDS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId/journals",
    element: withLayout(JournalsPage)({}),
    requiredActions: ["PARTICIPANT_JOURNALS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/screening-colors",
    element: withLayout(ScreeningColorsPage)({}),
    requiresAuth: false,
    requiredActions: ["COLOR_LIST_VIEW"],
  },
  {
    path: "/export",
    element: withLayout(ExportPage)({}),
    requiredActions: ["EXPORT_DATA_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/401",
    element: withLayout(Unauthorized, true)({}),
    requiresAuth: false,
  },
  {
    path: "/403",
    element: withLayout(Forbidden, true)({}),
    requiresAuth: false,
  },
  {
    path: "/404",
    element: withLayout(NotFound, true)({}),
    requiresAuth: false,
  },
];

export const isKnownRoute = (path: string): boolean => {
  const routePatterns = routesConfig.flatMap((route: RouteItem) => {
    const childPatterns =
      route.children
        ?.filter((child) => child.element)
        .map((child) => `${route.path.replace(/\/$/, "")}/${child.path}`) || [];

    return route.element ? [route.path, ...childPatterns] : childPatterns;
  });

  const regexPatterns = routePatterns.map(
    (routePattern) => new RegExp(`^${routePattern.replace(/:\w+/g, "\\w+")}$`)
  );

  return regexPatterns.some((regex) => regex.test(path));
};
